<script setup lang="ts">
import { computed } from 'vue'
import SiteBrandSymbol from '../../SiteBrandSymbol.vue'
import { resolveHeaderActions } from '../../header-actions'
import type { HeaderBlock } from '../../types'
import SiteHeaderActions from '../../SiteHeaderActions.vue'
import SiteLink from '../../SiteLink.vue'
import SiteNavigation from '../../SiteNavigation.vue'
import { useWebsiteNavigation } from '../../website-navigation-context'

const props = defineProps<{ block: HeaderBlock }>()
const brandLink = computed(() => props.block.content.brandLink ?? {
  label: props.block.content.businessName,
  destination: { type: 'page' as const, path: '/' },
})
const navigation = useWebsiteNavigation(
  () => props.block.content.navigationId,
  () => props.block.content.navigation,
)
const actions = computed(() => resolveHeaderActions(props.block.content))
const logoStyle = computed(() => {
  const width = props.block.content.logo?.width

  return { width: `var(--site-logo-width, ${typeof width === 'number' && width > 0 ? `${width}px` : '170px'})`, maxWidth: '100%' }
})
const brandLockupClass = computed(() => props.block.content.logo?.symbol ? 'header-centered__lockup' : '')
const headerClasses = computed(() => ({
  'site-header--floating': props.block.settings.floating,
  'site-header--glass': props.block.settings.surface === 'glass',
  'site-header--sticky': props.block.settings.sticky,
  'site-header--transparent': props.block.settings.surface === 'transparent',
}))

</script>

<template>
  <header
    class="site-header site-header--light"
    :class="headerClasses"
  >
    <div class="header-centered__inner site-header__surface site-site-container site:px-4 site:py-3">
      <div class="header-centered__navigation">
        <SiteHeaderActions :actions="actions" mode="small" />
        <SiteNavigation :navigation="navigation" />
      </div>
      <SiteLink class="header-centered__brand site-heading site:text-xl site:font-bold" :class="brandLockupClass" :link="brandLink">
        <SiteBrandSymbol v-if="block.content.logo?.symbol" :logo="block.content.logo" :name="block.content.brandLink?.label ?? block.content.businessName" />
        <img
          v-else-if="block.content.logo"
          :src="block.content.logo.src"
          :alt="block.content.logo.alt"
          :style="logoStyle"
          class="site:h-auto site:max-w-full site:object-contain"
        />
        <template v-else>{{ block.content.brandLink?.label ?? block.content.businessName }}</template>
      </SiteLink>
      <div class="header-centered__primary">
        <SiteHeaderActions :actions="actions" mode="desktop" />
      </div>
    </div>
  </header>
</template>

<style scoped>
.header-centered__inner {
  display: flex;
  min-height: 5rem;
  align-items: center;
  gap: 1.25rem;
}

.header-centered__navigation {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  order: 2;
  margin-left: auto;
}

.header-centered__brand {
  order: 1;
}

.header-centered__lockup {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.header-centered__primary {
  display: none;
}

@container site-renderer (min-width: 64rem) {
  .header-centered__inner {
    display: grid;
    min-height: 7rem;
    grid-template-columns: 1fr auto 1fr;
  }

  .header-centered__navigation {
    order: initial;
    margin-left: 0;
  }

  .header-centered__brand {
    order: initial;
    text-align: center;
    font-size: 1.5rem;
  }

  .header-centered__primary {
    display: flex;
    justify-content: flex-end;
  }
}
</style>
